import { useDebounce } from 'use-debounce';
import { Box, Button, Card, Container, Dialog, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import { getPrograms } from '../../api/services/program';
import { Program } from '../../api/interfaces/Program';
import { Term } from '../../api/interfaces/Terms';
import { createTerm, deleteTerm, getTerms, updateTerm } from '../../api/services/terms';
import moment from 'moment';
import { PinnacleBasicTable } from '../../components/PinnacleBasicTable/PinnacleBasicTable';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import CreateTermDrawerContent from './partials/CreateTermDrawerContent';
import EditTermDrawerContent from './partials/EditTermDrawerContent';
import { getModifiedFieldsOnly } from '../../utils/getModifiedFieldsOnly';
import { useTermForm } from './hooks/useTermForm';
import { PinnacleSearchField } from '../../components/PinnacleSearchField/PinnacleSearchField';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';

export interface FormData {
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    programId: string;
}

const termsColumn = [
    { key: 'name', label: 'Name' },
    {
        key: 'description',
        label: 'Description',
        sx: { maxWidth: 250 },
        render: (row: Term) => (
            <span style={{ display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                {row.description}
            </span>
        )
    },
    { key: 'programName', label: 'Program' },
    {
        key: 'startDate',
        label: 'Start Date',
        render: (row: Term) => moment(row.startDate).format('MM-DD-YYYY')
    },
    {
        key: 'endDate',
        label: 'End Date',
        render: (row: Term) => moment(row.endDate).format('MM-DD-YYYY')
    }
];

const TermsPage: React.FC = () => {
    const navigate = useNavigate();

    // Initial form data
    const initialFormData = {
        programId: '',
        startDate: '',
        endDate: '',
        name: '',
        description: '',
    };

    // Initial touch fields for error validation
    const initialTouchFields = {
        name: false,
        description: false,
        startDate: false,
        endDate: false,
        programId: false
    };

    // Hooks for form and validation states
    const {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid
    } = useTermForm({
        initialFormData,
    });

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [programs, setPrograms] = useState<Program[]>([]);
    const [terms, setTerms] = useState<Term[]>([]);

    // Search term with debounce
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const [isSearching, setIsSearching] = useState(false);

    // Drawable states for create and edit
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    // Set the selected term id for edit state
    const [selectedTermId, setSelectedTermId] = useState<string>('');
    const [selectedTermTitle, setSelectedTermTitle] = useState<string>('');

    // This will be used in the edit wherein we will identify the textfields
    // changes compare by comparing to this original form data
    const [originalFormData, setOriginalFormData] = useState(initialFormData);

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handle the opening and closing of toggle
    const toggleDrawer = (open: boolean, state: 'create' | 'edit' | string) => () => {
        if (open) {
            setCurrentDrawableState(state);
            setCurrentDrawableTitle(state === 'create' ? 'Add New Term' : 'Edit Term Information');
        } else {
            if (state === 'edit') {
                resetForm();
            }
            setCurrentDrawableState('');
        }

        setIsDrawerOpen(open);
    };

    const fetchTerms = useCallback(async () => {
        try {
            const termsResponse = await getTerms();
            setTerms(termsResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchPrograms = useCallback(async () => {
        try {
            const programResponse = await getPrograms();
            setPrograms(programResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchSearchResults = useCallback(async () => {
        try {
            setIsSearching(true);
            const searchTermsResponse = await getTerms({ keyword: debouncedSearchTerm });
            setTerms(searchTermsResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsSearching(false);
        } finally {
            setIsSearching(false);
        }
    }, [debouncedSearchTerm]);

    const handleCreateSubmit = async () => {
        setIsSubmitting(true);
        try {
            const createResponse = await createTerm(formData);
            setTerms((prevTerms) => [createResponse.data, ...prevTerms]);
            pushNotification({
                details: createResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const modifiedFields = useMemo(
        () => getModifiedFieldsOnly(formData, originalFormData),
        [formData, originalFormData]
    );

    const isFormModified = Object.keys(modifiedFields).length > 0;

    const handleUpdateSubmit = async () => {
        setIsSubmitting(true);
        try {
            const updateResponse = await updateTerm(selectedTermId, modifiedFields);
            setTerms((prevTerms) =>
                prevTerms.map((term) =>
                    term._id === updateResponse.data._id ? updateResponse.data : term
                )
            );
            pushNotification({
                details: updateResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleEdit = (selectedTerm: Term) => {
        setSelectedTermId(selectedTerm._id);
        const formattedStart = moment(selectedTerm.startDate).format("YYYY-MM-DD");
        const formattedEnd = moment(selectedTerm.endDate).format("YYYY-MM-DD");

        const termData: FormData = {
            programId: selectedTerm.programId,
            startDate: formattedStart,
            endDate: formattedEnd,
            name: selectedTerm.name,
            description: selectedTerm.description,
        };

        setOriginalFormData(termData);
        resetForm(termData);
        toggleDrawer(true, 'edit')();
    };

    // Open the dialog propmt to confirm
    const handleDelete = (term: Term) => {
        setSelectedTermId(term._id);
        setSelectedTermTitle(term.name)
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteTerm(selectedTermId);
            setTerms(prevTerms => prevTerms.filter(term => term._id !== selectedTermId));
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedTermTitle('');
            setSelectedTermId('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    // Debouncing mechanism for search term
    useEffect(() => {
        const search = async () => {
            setIsSearching(true);
            try {
                if (debouncedSearchTerm.trim() !== '') {
                    await fetchSearchResults();
                } else {
                    await fetchTerms();
                }
            } finally {
                setIsSearching(false);
            }
        };

        search();
    }, [debouncedSearchTerm, fetchSearchResults, fetchTerms]);

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }

                setCurrentAdminName(firstName);
                await fetchTerms();
                await fetchPrograms();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            }
        };
        run();
    }, [fetchTerms, fetchPrograms, navigate]);

    return (
        <main className="main-content">
            <Sidebar />
            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Terms' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: '2'
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'create')}
                        >
                            New Term
                        </Button>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <PinnacleSearchField
                                id="searchTerms"
                                label="Search terms here..."
                                value={searchTerm}
                                onChange={setSearchTerm}
                                isLoading={isSearching}
                            />
                        </Box>

                    </Box>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            p: 3,
                            backgroundColor: '#fff',
                            boxShadow: '0px 4px 12px 8px rgba(211, 211, 211, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 3 }}>
                            List of terms
                        </Typography>


                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ opacity: isSearching ? 0.4 : 1 }}>
                                <PinnacleBasicTable
                                    columns={termsColumn}
                                    rows={terms}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    headerColor="#007F3D"
                                />
                            </Box>
                            {isSearching && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                />
                            )}
                        </Box>
                    </Card>
                </Container>
            </Box>

            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isDrawerOpen}
                onClose={() => toggleDrawer(false, currentDrawableState)()}
            >
                {currentDrawableState === 'create' && (
                    <CreateTermDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleCreateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                    />
                )}

                {currentDrawableState === 'edit' && (
                    <EditTermDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleUpdateSubmit}
                        isSubmitting={isSubmitting}
                        programs={programs}
                        isFormValid={isFormValid}
                        isFormModified={isFormModified}
                    />
                )}
            </PinnacleDrawer>

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Term"
                    itemToDelete={selectedTermTitle}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </main>
    );
};

export default TermsPage;
